import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ rating, size = 15 }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size}
              fill={n <= Math.round(rating) ? "#F6B100" : "none"}
              color={n <= Math.round(rating) ? "#F6B100" : "var(--border)"} />
      ))}
    </span>
  );
}

export default function GoogleReviewsSection() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/google-reviews`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data || !data.configured || !data.rating) return null;
  const reviews = (data.reviews || []).filter((r) => r.text);

  return (
    <section style={{ background: "var(--surface)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}
             data-testid="home-google-reviews">
      <div className="px-4 sm:px-6 md:px-10 lg:px-12 py-20 sm:py-24 max-w-[1280px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] font-bold" style={{ color: "var(--primary)" }}>Reviews</p>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium mt-3" style={{ color: "var(--ink)" }}>
              What our clients say
            </h2>
            <div className="flex items-center gap-3 mt-4" data-testid="google-reviews-summary">
              <span className="font-display text-3xl font-medium" style={{ color: "var(--ink)" }}>
                {Number(data.rating).toFixed(1)}
              </span>
              <div>
                <Stars rating={data.rating} />
                <p className="text-xs mt-0.5" style={{ color: "var(--muted-fg)" }}>
                  {data.user_rating_count} review{data.user_rating_count === 1 ? "" : "s"} on Google
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {data.maps_url && (
              <a href={data.maps_url} target="_blank" rel="noreferrer" className="btn-ghost"
                 data-testid="google-reviews-see-all">
                See all on Google <ExternalLink size={13} />
              </a>
            )}
            <a href={data.write_review_url} target="_blank" rel="noreferrer" className="btn-primary"
               data-testid="google-reviews-write-btn">
              Review us on Google
            </a>
          </div>
        </div>

        {reviews.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {reviews.map((r, i) => (
              <div key={i} className="surface p-6 flex flex-col" style={{ border: "1px solid var(--border)" }}
                   data-testid={`google-review-card-${i}`}>
                <Stars rating={r.rating} size={14} />
                <p className="text-sm mt-3 leading-relaxed flex-1" style={{ color: "var(--body)" }}>
                  {r.text}
                </p>
                <div className="flex items-center gap-3 mt-5 pt-4" style={{ borderTop: "1px solid var(--border)" }}>
                  {r.author_photo ? (
                    <img src={r.author_photo} alt="" loading="lazy" referrerPolicy="no-referrer"
                         className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <span className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold"
                          style={{ background: "var(--muted)", color: "var(--ink)" }}>
                      {(r.author_name || "G")[0]}
                    </span>
                  )}
                  <div className="min-w-0">
                    {r.author_url ? (
                      <a href={r.author_url} target="_blank" rel="noreferrer"
                         className="text-sm font-medium truncate block hover:underline" style={{ color: "var(--ink)" }}>
                        {r.author_name}
                      </a>
                    ) : (
                      <p className="text-sm font-medium truncate" style={{ color: "var(--ink)" }}>{r.author_name}</p>
                    )}
                    <p className="text-xs" style={{ color: "var(--muted-fg)" }}>{r.relative_time}</p>
                  </div>
                  {r.review_url && (
                    <a href={r.review_url} target="_blank" rel="noreferrer" className="ml-auto text-xs hover:underline"
                       style={{ color: "var(--muted-fg)" }} data-testid={`google-review-link-${i}`}>
                      View on Google
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs mt-8" style={{ color: "var(--muted-fg)" }}>
          Reviews provided by Google Maps, shown by relevance.
        </p>
      </div>
    </section>
  );
}
